import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useState } from 'react';
import { Permissions } from '@shiftly/shared/src/permissions';
import { useUnreadCount } from '../lib/supabase/notifications/supabaseNotificationService';
import { Bell, ChevronDown, ChevronRight, Loader2, CircleDollarSign, User, LogOut, LayoutDashboard, Calendar, DollarSign, Settings, Users, Ticket, UserCheck, Clock, HelpCircle } from 'lucide-react';
import NewLogo from '../assets/new-logo.svg';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import FeedbackPopup from './FeedbackPopup';

type NavItem = {
  path: string;
  label: string;
  permission: string | string[];
  showInPayView?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
};

type NavGroup = {
  title: string;
  items: NavItem[];
  forceDropdown?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
};

const NavigationGroup = ({ 
  group, 
  permissions, 
  isPayView, 
  getLinkClass 
}: { 
  group: NavGroup;
  permissions: string[];
  isPayView: boolean;
  getLinkClass: (path: string) => string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Filter items based on permissions and pay view
  const availableItems = group.items.filter(item => {
    const hasPermission = Array.isArray(item.permission)
      ? item.permission.some(p => permissions.includes(p))
      : permissions.includes(item.permission);
    return hasPermission && (!isPayView || item.showInPayView);
  });

  if (availableItems.length === 0) return null;
  if (availableItems.length === 1 && !group.forceDropdown) {
    return (
      <Link to={availableItems[0].path} className={getLinkClass(availableItems[0].path)}>
        {availableItems[0].label}
      </Link>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className={`px-2.5 py-2 rounded-md text-sm font-medium text-foreground hover:bg-brand-light hover:text-brand-light-foreground dark:text-muted-foreground dark:hover:bg-brand-light dark:hover:text-brand-light-foreground transition-colors flex items-center gap-1.5`}
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          {group.icon && <group.icon className="w-4 h-4" />}
          <span>{group.title}</span>
          <ChevronDown className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-64 p-2" 
        align="start"
        sideOffset={0}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <div className="space-y-1">
          {availableItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className="block w-full px-3 py-2 text-sm rounded-md hover:bg-brand-light hover:text-brand-light-foreground transition-colors whitespace-nowrap"
              onClick={() => setIsOpen(false)}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const MobileSubmenu = ({ 
  title, 
  children, 
  icon: Icon,
  defaultOpen = false 
}: { 
  title: string; 
  children: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  defaultOpen?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 h-8 text-sm font-medium text-foreground hover:bg-brand-light hover:text-brand-light-foreground dark:text-muted-foreground dark:hover:bg-brand-light dark:hover:text-brand-light-foreground"
      >
        <span className="flex items-center">
          {Icon && <Icon className="w-4 h-4 mr-2" />}
          {title}
        </span>
        <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
        <div className="pl-3 border-l border-brand-border dark:border-brand-border ml-3">
          {children}
        </div>
      </div>
    </div>
  );
};

const FloatingFeedbackButton = ({ 
  permissions, 
  onToggle,
  isPopupOpen
}: { 
  permissions: string[];
  onToggle: () => void;
  isPopupOpen: boolean;
}) => {
  if (!permissions.includes(Permissions.NAVIGATION_SUPPORT)) {
    return null;
  }

  return (
    <div className={`fixed right-6 bottom-6 z-50 transition-all duration-&lsqb;1200ms&rsqb; ease-out ${
      isPopupOpen ? 'opacity-0 scale-0 pointer-events-none' : 'opacity-100 scale-100'
    }`}>
      <button
        onClick={onToggle}
        className={`flex items-center justify-center w-14 h-14 bg-brand-light hover:bg-brand-light/90 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-&lsqb;1000ms&rsqb; ease-out hover:scale-110 ${
          isPopupOpen ? 'scale-150 opacity-0' : 'scale-100 opacity-100'
        }`}
        title="Feedback & Support"
      >
        <HelpCircle className={`w-6 h-6 transition-all duration-&lsqb;800ms&rsqb; ease-out ${
          isPopupOpen ? 'scale-0 rotate-180' : 'scale-100 rotate-0'
        }`} />
      </button>
    </div>
  );
};

const Navigation = () => {
  const { user, userData, permissions, logout, loading } = useAuth();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPayView, setIsPayView] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const { data: unreadCount = 0 } = useUnreadCount(user?.id || '');

  // Close mobile menu when location changes
  React.useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const coreNavItems: NavItem[] = [
    { path: '/users', label: 'Users', permission: Permissions.NAVIGATION_USERS, showInPayView: true, icon: Users },
    { path: '/models', label: 'Models', permission: Permissions.NAVIGATION_MODELS, showInPayView: true, icon: UserCheck },
    { path: '/tickets', label: 'Tickets', permission: Permissions.NAVIGATION_TICKETS, showInPayView: true, icon: Ticket },
  ];

  const navigationGroups: NavGroup[] = [
    {
      title: 'Rota',
      icon: Calendar,
      items: [
        { path: '/rota', label: 'Sales Rota', permission: Permissions.NAVIGATION_ROTA, showInPayView: true },
        { path: '/scheduling-rota', label: 'Scheduling Rota', permission: Permissions.SCHEDULE_EDIT, showInPayView: true },
        { path: '/gg-swap-rota', label: 'GG Swap Rota', permission: Permissions.GG_SWAP_MANAGE, showInPayView: true },
        { path: '/spam-shift-rota', label: 'Spam Shift Rota', permission: Permissions.SPAM_SHIFTS_MANAGE, showInPayView: true },
        { path: '/supervisor-rota', label: 'Supervisor Rota', permission: Permissions.SUPERVISOR_SHIFTS_MANAGE, showInPayView: true },
      ]
    },
    {
      title: 'Holiday & OT',
      icon: Clock,
      items: [
        { path: '/holidays', label: 'Holidays', permission: Permissions.HOLIDAYS_VIEW },
        { path: '/time-off', label: 'Time Off Overview', permission: Permissions.HOLIDAYS_MANAGE },
        { path: '/overtime', label: 'Overtime', permission: Permissions.OVERTIME_REQUEST },
        { path: '/overtime-management', label: 'Overtime Requests', permission: Permissions.OVERTIME_MANAGE },
      ]
    },
    {
      title: 'Finance & Pay',
      icon: DollarSign,
      forceDropdown: true,
      items: [
        { path: '/pay-exceptions', label: 'Pay Exceptions', permission: Permissions.SCHEDULE_EDIT, showInPayView: true },
        {
          path: '/payment-files',
          label: 'Payment Files',
          permission: [
            Permissions.SHEETS_PAY_SUMMARY,
            Permissions.SHEETS_MODEL_PAY_SUMMARY,
            Permissions.SHEETS_INVOICES,
            Permissions.SHEETS_MODEL_PPV,
            Permissions.SHEETS_STAFF_REPORT,
            Permissions.SHEETS_MODEL_EARNINGS_REPORT,
            Permissions.SHEETS_MODEL_STATISTICS,
            Permissions.SHEETS_FAN_GAIN,
            Permissions.SHEETS_FAN_GAIN_PER_AD,
          ],
          showInPayView: true
        },
        { path: '/hours-validation', label: 'Hours Validation', permission: Permissions.PAY_VIEW_OTHERS, showInPayView: true },
        { path: '/exchange-rates', label: 'Exchange Rates', permission: Permissions.NAVIGATION_EXCHANGE_RATES, showInPayView: true },
        { path: '/misc-payments', label: 'Misc Payments', permission: Permissions.PAY_MISC_PAYMENTS, showInPayView: true },
        { path: '/costs', label: 'Costs', permission: Permissions.NAVIGATION_COSTS, showInPayView: true },
        { path: '/referrals', label: 'Referrals', permission: Permissions.PAY_REFERRALS, showInPayView: true },
      ]
    },
    {
      title: 'Admin',
      icon: Settings,
      forceDropdown: true,
      items: [
        { path: '/roles', label: 'Roles', permission: Permissions.NAVIGATION_ROLES },
        { path: '/weekend-staffing', label: 'Weekend Staffing', permission: Permissions.NAVIGATION_WEEKEND_STAFFING },
        { path: '/audit-logs', label: 'Audit Logs', permission: Permissions.NAVIGATION_AUDIT_LOGS },
        { path: '/promo-distribution', label: 'Model Promo Distribution', permission: Permissions.NAVIGATION_PROMO_DISTRIBUTION },
        { path: '/overwatch-requests', label: 'Overwatch Requests', permission: Permissions.REQUESTS_VIEW_OVERWATCH },
        { path: '/format-list', label: 'Format List', permission: Permissions.SCHEDULE_VIEW_FORMAT_LIST },
      ]
    }
  ];

  const getLinkClass = (path: string) => {
    // For root path, use exact match
    if (path === '/') {
      return `px-2.5 py-2 rounded-md text-sm font-medium ${
        location.pathname === '/' 
          ? 'bg-brand-light text-white dark:bg-brand-light dark:text-white' 
          : 'text-foreground hover:bg-brand-light hover:text-brand-light-foreground dark:text-muted-foreground dark:hover:bg-brand-light dark:hover:text-brand-light-foreground'
      } transition-colors`;
    }
    
    // For other paths, check if current path starts with the nav path and ensure it's a proper path segment
    const isActive = location.pathname.startsWith(path) && 
      (location.pathname === path || 
       location.pathname.startsWith(`${path}/`));
       
    return `px-2.5 py-2 rounded-md text-sm font-medium ${
      isActive 
        ? 'bg-brand-light text-white dark:bg-brand-light dark:text-white' 
        : 'text-foreground hover:bg-brand-light hover:text-brand-light-foreground dark:text-muted-foreground dark:hover:bg-brand-light dark:hover:text-brand-light-foreground'
    } transition-colors`;
  };

  const getMobileLinkClass = (path: string) => {
    const isActive = location.pathname === path;
    return `w-full px-4 py-2 text-left text-sm font-medium ${
      isActive 
        ? 'bg-brand-light text-white dark:bg-brand-light dark:text-white' 
        : 'text-foreground hover:bg-brand-light hover:text-brand-light-foreground dark:text-muted-foreground dark:hover:bg-brand-light dark:hover:text-brand-light-foreground'
    } transition-colors`;
  };

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-16">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  // Don't render anything if user is not authenticated
  if (!user) {
    return null;
  }

  return (
    <>
      <nav className="sticky top-0 z-40 w-full backdrop-blur-lg bg-white/50 dark:bg-brand-dark/50 border-b border-brand-border/50 dark:border-brand-border/50">
        <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <Link 
              to="/" 
              className="text-xl font-bold bg-clip-text text-transparent text-white"
            >
              <img src={NewLogo} alt="IH Shifts" className="w-40" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {user ? (
              <>
                {/* Core Navigation Items */}
                {coreNavItems.map(item => {
                  const hasPermission = Array.isArray(item.permission)
                    ? item.permission.some(p => permissions.includes(p))
                    : permissions.includes(item.permission);
                  return hasPermission && (!isPayView || item.showInPayView) && (
                    <Link key={item.path} to={item.path} className={`${getLinkClass(item.path)} flex items-center gap-1.5`}>
                      {item.icon && <item.icon className="w-4 h-4" />}
                      {item.label}
                    </Link>
                  );
                })}
                
                {/* Navigation Groups */}
                {navigationGroups.map(group => (
                  <NavigationGroup
                    key={group.title}
                    group={group}
                    permissions={permissions}
                    isPayView={isPayView}
                    getLinkClass={getLinkClass}
                  />
                ))}

                {/* Pay View Toggle */}
                {permissions.includes(Permissions.PAY_VIEW_OTHERS) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsPayView(!isPayView)}
                    className={`flex items-center gap-1.5 px-2.5 py-2 rounded-md text-sm transition-all duration-200 ${
                      isPayView 
                        ? 'bg-brand-green/10 text-brand-green hover:bg-brand-green/20 dark:bg-brand-green/20 dark:text-brand-green dark:hover:bg-brand-green/30' 
                        : 'hover:bg-brand-light hover:text-brand-light-foreground text-foreground dark:text-muted-foreground dark:hover:bg-brand-light dark:hover:text-brand-light-foreground'
                    }`}
                    description={isPayView ? 'Exit Pay View' : 'Enter Pay View'}
                  >
                    <CircleDollarSign className={`h-4 w-4 ${isPayView ? 'text-brand-green dark:text-brand-green' : ''}`} />
                  </Button>
                )}

                {/* User Profile Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex items-center gap-1.5 px-2.5 py-2 rounded-md text-sm font-medium text-foreground hover:bg-brand-light hover:text-brand-light-foreground dark:text-muted-foreground dark:hover:bg-brand-light dark:hover:text-brand-light-foreground transition-colors"
                    >
                      <User className="w-4 h-4" />
                      <span>{userData?.full_name || user.user_metadata?.full_name || user.email}</span>
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64 bg-popover border border-brand-border text-popover-foreground">
                    <DropdownMenuItem asChild>
                      <Link to="/" className="flex items-center gap-2 w-full whitespace-nowrap">
                        <LayoutDashboard className="w-4 h-4" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2 w-full whitespace-nowrap">
                        <User className="w-4 h-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/notifications" className="flex items-center gap-2 w-full relative whitespace-nowrap">
                        <Bell className="w-4 h-4" />
                        Notifications
                        {unreadCount > 0 && (
                          <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-brand-red text-white rounded-full">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="flex items-center gap-2 text-destructive focus:text-destructive whitespace-nowrap">
                      <LogOut className="w-4 h-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login" className={getLinkClass('/login')}>
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-brand-light to-brand-pink text-white shadow-lg hover:from-brand-light/90 hover:to-brand-pink/90 hover:shadow-brand-light/20 dark:hover:shadow-brand-light/50 transition-all"
                >
                  Register
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            {permissions.includes(Permissions.PAY_VIEW_OTHERS) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsPayView(!isPayView)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                  isPayView 
                    ? 'bg-brand-green/10 text-brand-green hover:bg-brand-green/20 dark:bg-brand-green/20 dark:text-brand-green dark:hover:bg-brand-green/30' 
                    : 'hover:bg-brand-light hover:text-brand-light-foreground text-foreground dark:text-muted-foreground dark:hover:bg-brand-light dark:hover:text-brand-light-foreground'
                }`}
              >
                <CircleDollarSign className={`h-4 w-4 ${isPayView ? 'text-brand-green dark:text-brand-green' : ''}`} />
                <span className="text-sm font-medium">Pay View</span>
              </Button>
            )}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg hover:bg-brand-light hover:text-brand-light-foreground dark:hover:bg-brand-light dark:hover:text-brand-light-foreground transition-colors"
              aria-label="Toggle menu"
            >
              <svg
                className="w-5 h-5 text-foreground dark:text-muted-foreground"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div 
          className={`lg:hidden overflow-hidden transition-all duration-200 ease-in-out ${
            isMenuOpen ? 'max-h-[70vh] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="py-1">
            {user ? (
              <div className="space-y-1">
                {/* Core Navigation Items */}
                <div className="space-y-0.5 pb-1 border-b border-brand-border dark:border-brand-border">
                  {coreNavItems.map(item => {
                    const hasPermission = Array.isArray(item.permission)
                      ? item.permission.some(p => permissions.includes(p))
                      : permissions.includes(item.permission);
                    return hasPermission && (!isPayView || item.showInPayView) && (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center h-8 px-3 ${getMobileLinkClass(item.path)}`}
                      >
                        {item.icon && <item.icon className="w-4 h-4 mr-2" />}
                        {item.label}
                      </Link>
                    );
                  })}
                </div>

                {/* Mobile Navigation Groups */}
                {navigationGroups.map(group => {
                  const availableItems = group.items.filter(item => {
                    const hasPermission = Array.isArray(item.permission)
                      ? item.permission.some(p => permissions.includes(p))
                      : permissions.includes(item.permission);
                    return hasPermission && (!isPayView || item.showInPayView);
                  });

                  if (availableItems.length === 0) return null;

                  return availableItems.length === 1 && !group.forceDropdown ? (
                    <Link
                      key={group.title}
                      to={availableItems[0].path}
                      className={`flex items-center h-8 px-3 ${getMobileLinkClass(availableItems[0].path)}`}
                    >
                      {availableItems[0].label}
                    </Link>
                  ) : (
                    <MobileSubmenu key={group.title} title={group.title}>
                      {availableItems.map(item => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`flex items-center h-8 ${getMobileLinkClass(item.path)}`}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </MobileSubmenu>
                  );
                })}

                {/* User Profile Section */}
                <div className="space-y-0.5 pt-1 border-t border-brand-border dark:border-brand-border">
                  <Link to="/" className={`flex items-center h-8 px-3 ${getMobileLinkClass('/')}`}>
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Dashboard
                  </Link>
                  <Link to="/profile" className={`flex items-center h-8 px-3 ${getMobileLinkClass('/profile')}`}>
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </Link>
                  <Link to="/notifications" className={`flex items-center h-8 px-3 ${getMobileLinkClass('/notifications')} relative`}>
                    <Bell className="w-4 h-4 mr-2" />
                    Notifications
                    {unreadCount > 0 && (
                      <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-brand-red text-white rounded-full">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Link>
                  <button
                    onClick={logout}
                    className="w-full h-8 px-3 text-left text-sm font-medium text-destructive hover:bg-destructive/10 dark:text-destructive dark:hover:bg-destructive/10 flex items-center"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-3 py-1 space-y-1">
                <Link 
                  to="/login" 
                  className={`flex items-center h-8 ${getMobileLinkClass('/login')}`}
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="flex items-center justify-center w-full h-8 px-4 rounded-lg text-sm font-medium bg-gradient-to-r from-brand-light to-brand-pink text-white hover:from-brand-light/90 hover:to-brand-pink/90 transition-all"
                >
                  Register
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
    <FloatingFeedbackButton 
      permissions={permissions} 
      onToggle={() => setIsFeedbackOpen(!isFeedbackOpen)}
      isPopupOpen={isFeedbackOpen}
    />
    <FeedbackPopup 
      isOpen={isFeedbackOpen} 
      onClose={() => setIsFeedbackOpen(false)} 
    />
    </>
  );
};

export default Navigation; 